/**
 * Seitenrahmen und Spaltenraster fuer ALLE Seiten (Roadmap D-01, Leitlinie 1).
 *
 * Leitlinie 1: mehrspaltig auf dem Laptop, einspaltig auf dem Handy - ohne
 * Ausnahme. Frueher war jede Seite eine schmale, zentrierte Spalte
 * (max-w-2xl bis max-w-5xl); auf einem breiten Bildschirm blieb mehr als die
 * Haelfte leer, und alles stand untereinander.
 *
 * Leitlinie 3: gleichartige Seiten folgen derselben Vorlage. Deshalb gibt es
 * genau EINE Seitenbreite und eine kleine, feste Auswahl an Rastern, statt
 * dass jede Seite eigene Spaltenklassen erfindet. Eine neue Seite nimmt
 * `Page` und eines der Raster - dann ist sie automatisch richtig.
 *
 * Umbruchpunkt ist 900 px (D-00): darunter eine Spalte, darueber mehrere.
 */
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils/cn';

/** Aeusserer Rahmen jeder Seite: einheitliche Breite und Abstaende. */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[100rem] flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Die Raster, aus denen Seiten waehlen.
 *
 * - `haupt-seite`: breite Hauptspalte, schmalere Nebenspalte (2:1).
 *   Fuer Seiten mit einem Arbeitsbereich und begleitenden Informationen.
 * - `seite-haupt`: schmale Spalte links (Filter, Navigation), breiter Inhalt.
 * - `drei`: drei gleich breite Spalten, auf mittleren Laptops zwei.
 * - `zwei`: zwei gleich breite Spalten.
 * - `karten`: Kartenraster, das sich selbst fuellt - fuer Listen gleichartiger
 *   Eintraege (Projekte, Ziele, Buecher, Bereiche).
 */
export type PageGridLayout = 'haupt-seite' | 'seite-haupt' | 'drei' | 'zwei' | 'karten';

/*
 * Der Abstand gehoert zum Raster und wird nicht per `className` ueberschrieben:
 * `cn` fuegt nur zusammen, zwei gap-Klassen wuerden sich zufaellig schlagen.
 */
const LAYOUTS: Record<PageGridLayout, string> = {
  'haupt-seite': 'gap-5 min-[900px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]',
  'seite-haupt': 'gap-5 min-[900px]:grid-cols-[minmax(14rem,1fr)_minmax(0,3fr)]',
  drei: 'gap-5 min-[900px]:grid-cols-2 xl:grid-cols-3',
  zwei: 'gap-5 min-[900px]:grid-cols-2',
  karten: 'gap-3 sm:grid-cols-2 xl:grid-cols-3',
};

/**
 * `as="ul"` fuer Listen, deren Eintraege selbst `<li>` sind (Karten) - so
 * bleibt die Listen-Semantik fuer Screenreader erhalten.
 */
export function PageGrid({
  layout, children, className, as: Element = 'div',
}: {
  layout: PageGridLayout;
  children: ReactNode;
  className?: string;
  as?: 'div' | 'ul';
}) {
  return (
    // Einspaltig `minmax(0,1fr)`: breite Inhalte (Tabellen mit Mindestbreite) scrollen in sich, statt die Spalte zu sprengen.
    <Element className={cn('grid grid-cols-1 items-start', LAYOUTS[layout], className)}>
      {children}
    </Element>
  );
}

/** Eine Spalte im Raster - stapelt ihren Inhalt mit einheitlichem Abstand. */
export function PageColumn({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex min-w-0 flex-col gap-5', className)}>{children}</div>;
}
